import type { SocketSubscription, VehicleCounts } from '@traintopia/contracts'
import { focusedRouteIds, useTransitStore } from '../store/transit'
import { useRiderStore } from '../store/rider'
import { useSystemStore, type SystemStatus } from '../store/system'
import { routeFamily } from '../ui/colors'
import { WS_URL } from './env'
import { noteServerTime } from './serverClock'

let socket: WebSocket | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let reconnectDelay = 1000

function clearReconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
}

/**
 * What this client needs pushed to it — exactly what the map draws.
 *
 * With a focus (a Routes-panel selection or an active trip plan) the map draws only those
 * lines, of any fleet, so that is the subscription. Without one it draws whole fleets — the
 * mode filter — so those are sent whole, and nothing else: the MTA bus and NJ Transit fleets
 * are ~1.6 MB a cycle that a subway-only map would download and discard.
 *
 * Either way the boarded vehicle's line is added. Without it, boarding a train outside the
 * focus (an off-plan train while a trip is drawn), or a bus while the map shows subways,
 * stops its updates, and the ride the user is actually on goes stale and ages out of the
 * store.
 *
 * Route lists still show "N active" for every fleet: those come from VEHICLE_COUNTS, which
 * every client is sent whatever it subscribes to.
 *
 * Alerts: only those in effect now (`alerts: 'live'`). Upcoming planned work is loaded over
 * REST when the Alerts sheet shows it (`loadUpcomingAlerts`).
 */
export function clientSubscription(): SocketSubscription {
  const state = useTransitStore.getState()
  const focus = focusedRouteIds(state)
  const boarded = useRiderStore.getState().trackedVehicle
  const ride = boarded ? routeFamily(boarded.routeId) : []
  if (focus.size > 0) return { routeIds: [...new Set([...focus, ...ride])], alerts: 'live' }
  return { modes: [...state.transitFilter], ...(ride.length ? { routeIds: ride } : {}), alerts: 'live' }
}

export function connectWebSocket(url: string = WS_URL): void {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return
  }

  clearReconnect()

  try {
    socket = new WebSocket(url)
  } catch {
    scheduleReconnect(url)
    return
  }

  socket.onopen = () => {
    useTransitStore.getState().setWsConnected(true)
    reconnectDelay = 1000
    // Sent on every (re)connect, before the server's snapshot reads finish, so the snapshot
    // is already filtered to what this client draws.
    sendSubscription()
  }

  socket.onmessage = (event: MessageEvent) => {
    try {
      const msg = JSON.parse(event.data as string) as {
        type: string
        data?: unknown
        timestamp?: number
      }

      if (typeof msg.timestamp === 'number') noteServerTime(msg.timestamp)
      const store = useTransitStore.getState()
      switch (msg.type) {
        case 'VEHICLE_POSITIONS':
          store.upsertVehicles((msg.data as { vehicles: Parameters<typeof store.upsertVehicles>[0] }).vehicles)
          break
        case 'VEHICLE_COUNTS':
          store.setVehicleCounts(msg.data as VehicleCounts)
          break
        case 'VEHICLE_UPDATE':
          store.upsertVehicle(msg.data as Parameters<typeof store.upsertVehicle>[0])
          break
        case 'SERVICE_ALERTS':
          store.setAlerts((msg.data as { alerts: Parameters<typeof store.setAlerts>[0] }).alerts)
          break
        case 'SYSTEM_STATUS':
          useSystemStore.getState().setStatus(msg.data as SystemStatus)
          break
        case 'HEARTBEAT':
          break
      }
    } catch {
      // ignore malformed frames
    }
  }

  socket.onerror = () => {
    socket?.close()
  }

  socket.onclose = (event) => {
    useTransitStore.getState().setWsConnected(false)
    socket = null
    if (event.code !== 1000 && event.code !== 1001) {
      scheduleReconnect(url)
    }
  }
}

function scheduleReconnect(url: string) {
  clearReconnect()
  reconnectTimer = setTimeout(() => {
    reconnectDelay = Math.min(reconnectDelay * 2, 30_000)
    connectWebSocket(url)
  }, reconnectDelay)
}

/** The whole subscription goes in ONE message, which REPLACES the server's last: sending a
 *  message per route used to leave it holding only the last one. The server answers a
 *  widened subscription with the vehicles it adds, so a mode switched on fills in at once. */
export function sendSubscription(): void {
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ type: 'SUBSCRIBE', ...clientSubscription() }))
  }
}

export function disconnectWebSocket(): void {
  clearReconnect()
  if (socket) {
    socket.close(1000)
    socket = null
  }
  useTransitStore.getState().setWsConnected(false)
}
